(function (global) {
  'use strict';

  const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

  const SEMANTIC_PROFILE_MAP = {
    // Universal
    universal: 'universal',
    'universal adapter': 'universal',
    'profile a': 'universal',
    'profile a universal adapter': 'universal',
    a: 'universal',

    // Type G
    type_g: 'type_g',
    'type g': 'type_g',
    'type g uk': 'type_g',
    'type g adapter': 'type_g',
    'type g uk adapter': 'type_g',
    'profile b': 'type_g',
    'profile b uk type g adapter': 'type_g',
    'profile b type g uk adapter': 'type_g',
    b: 'type_g',

    // Type B
    type_b: 'type_b',
    'type b': 'type_b',
    'type b us': 'type_b',
    'type b usa': 'type_b',
    'type b amerika': 'type_b',
    'type b adapter': 'type_b',

    // Type M
    type_m: 'type_m',
    'type m': 'type_m',
    'type m south africa': 'type_m',
    'type m zuid afrika': 'type_m',
    'type m adapter': 'type_m',

    // Type D
    type_d: 'type_d',
    'type d': 'type_d',
    'type d adapter': 'type_d',

    // Custom Include
    custom_include: 'custom_include',
    'custom compatible countries only': 'custom_include',
    'custom include': 'custom_include',
    'profile c': 'custom_include',
    'profile c custom compatible countries only': 'custom_include',
    'profile c custom include only': 'custom_include',
    c: 'custom_include',

    // Custom Exclude
    custom_exclude: 'custom_exclude',
    'custom excluded countries only': 'custom_exclude',
    'custom exclude': 'custom_exclude',
    'profile d': 'custom_exclude',
    'profile d custom excluded countries only': 'custom_exclude',
    'profile d custom universal exclude': 'custom_exclude',
    d: 'custom_exclude'
  };

  const PRESET_PLUGS = {
    type_g: 'G',
    type_b: 'B',
    type_m: 'M',
    type_d: 'D'
  };

  const DEFAULT_COPY = {
    en: {
      title: 'Travel Checker', label: 'Is this travel adapter suitable for:', placeholder: 'Search for a country',
      clear: 'Clear country search', empty: 'No countries found.', unavailable: 'Compatibility is currently unavailable for this product.',
      yes: 'Yes', no: 'No', warning: 'Please Check', noTemplate: 'No. This country requires [[plugs]].',
      warningTemplate: 'Please check: this country uses [[plugs]]. Check with your hotel, resort, accommodation, or travel guide to find out which type you will need.',
      selected: 'Selected country: {{country}}', results: 'Country results', search: 'Search countries'
    },
    nl: {
      title: 'Reis Checker', label: 'Is deze reisstekker geschikt voor:', placeholder: 'Zoek een land',
      clear: 'Wis landzoekopdracht', empty: 'Geen landen gevonden.', unavailable: 'Compatibiliteit is momenteel niet beschikbaar voor dit product.',
      yes: 'Ja', no: 'Nee', warning: 'Controleer', noTemplate: 'Nee. Dit land heeft [[plugs]] nodig.',
      warningTemplate: 'Controleer: dit land gebruikt [[plugs]]. Vraag uw hotel, resort, accommodatie of reisgids welk type u nodig hebt.',
      selected: 'Geselecteerd land: {{country}}', results: 'Landresultaten', search: 'Zoek landen'
    }
  };

  /** @typedef {{id:string,name_en:string,name_nl:string,iso:string,plugs:string[],is_please_check:boolean,aliases?:string[]}} TravelCheckerCountry */
  /** @typedef {{state:'yes'|'no'|'warning'|'unavailable',reason:string,profile:string|null,country:TravelCheckerCountry|null,plugs:string[]}} TravelCheckerResult */

  function normalize(value) {
    return String(value == null ? '' : value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim().replace(/\s+/g, ' ');
  }

  function key(value) {
    return normalize(value).replace(/[^a-z0-9]+/g, '');
  }

  function localeFor(value) {
    return normalize(value).startsWith('nl') ? 'nl' : 'en';
  }

  function normalizeProfile(value) {
    const candidate = normalize(value).replace(/[—–:/-]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (SEMANTIC_PROFILE_MAP[candidate]) return SEMANTIC_PROFILE_MAP[candidate];
    return null;
  }

  function parseCountryTokens(value) {
    const source = Array.isArray(value) ? value : [value];
    const tokens = source.flatMap((item) => String(item == null ? '' : item).split(/[,;\n\r]+/));
    const seen = new Set();
    return tokens.reduce((result, token) => {
      const trimmed = token.trim();
      const tokenKey = key(trimmed);
      if (tokenKey && !seen.has(tokenKey)) {
        seen.add(tokenKey);
        result.push(trimmed);
      }
      return result;
    }, []);
  }

  function shouldRenderForConfig(rawConfig) {
    const profile = normalizeProfile(rawConfig && rawConfig.mode);
    if (!profile) return false;
    if (['universal', 'type_g', 'type_b', 'type_m', 'type_d'].includes(profile)) return true;
    if (['custom_include', 'custom_exclude'].includes(profile)) {
      return parseCountryTokens(rawConfig && rawConfig.customCountries).length > 0;
    }
    return false;
  }

  function isUsableDataset(dataset) {
    if (!dataset || !Array.isArray(dataset.countries) || !dataset.countries.length || !dataset.profiles) return false;
    if (!Array.isArray(dataset.profiles.universal_exclusion_ids) || !Array.isArray(dataset.profiles.profile_b_country_ids)) return false;
    return dataset.countries.every((country) => country && country.id && country.name_en && country.name_nl && country.iso && Array.isArray(country.plugs) && country.plugs.length);
  }

  function buildIndexes(dataset) {
    const index = {
      byId: new Map(),
      byEnglishName: new Map(),
      byDutchName: new Map(),
      byUniqueIso: new Map(),
      ambiguousIso: new Map(),
      byAlias: new Map()
    };
    const isoGroups = new Map();
    for (const country of dataset.countries || []) {
      index.byId.set(country.id, country);
      index.byEnglishName.set(key(country.name_en), country);
      index.byDutchName.set(key(country.name_nl), country);
      const iso = key(country.iso);
      const group = isoGroups.get(iso) || [];
      group.push(country);
      isoGroups.set(iso, group);

      for (const alias of country.aliases || []) {
        const aliasKey = key(alias);
        if (aliasKey) {
          const list = index.byAlias.get(aliasKey) || [];
          if (!list.some((c) => c.id === country.id)) {
            list.push(country);
          }
          index.byAlias.set(aliasKey, list);
        }
      }
    }
    for (const [iso, countries] of isoGroups) {
      if (countries.length === 1) index.byUniqueIso.set(iso, countries[0]);
      else index.ambiguousIso.set(iso, countries);
    }
    return index;
  }

  function resolveCountry(value, indexes) {
    if (value && typeof value === 'object' && value.id && indexes.byId.get(value.id)) {
      return { status: 'resolved', country: indexes.byId.get(value.id) };
    }
    const candidate = key(value);
    if (!candidate) return { status: 'empty' };
    if (indexes.byId.has(value)) return { status: 'resolved', country: indexes.byId.get(value) };
    if (indexes.byEnglishName.has(candidate)) return { status: 'resolved', country: indexes.byEnglishName.get(candidate) };
    if (indexes.byDutchName.has(candidate)) return { status: 'resolved', country: indexes.byDutchName.get(candidate) };
    if (indexes.ambiguousIso.has(candidate)) return { status: 'ambiguous-iso', candidates: indexes.ambiguousIso.get(candidate) };
    if (indexes.byUniqueIso.has(candidate)) return { status: 'resolved', country: indexes.byUniqueIso.get(candidate) };

    if (indexes.byAlias && indexes.byAlias.has(candidate)) {
      const aliasMatches = indexes.byAlias.get(candidate);
      if (aliasMatches.length === 1) return { status: 'resolved', country: aliasMatches[0] };
      return { status: 'ambiguous-alias', candidates: aliasMatches };
    }

    return { status: 'unknown' };
  }

  function prepareConfiguration(rawConfig, indexes) {
    const profile = normalizeProfile(rawConfig && rawConfig.mode);
    if (!profile) return { valid: false, reason: 'invalid-mode', profile: null, countries: new Set() };
    if (['universal', 'type_g', 'type_b', 'type_m', 'type_d'].includes(profile)) {
      return { valid: true, profile, countries: new Set() };
    }
    const tokens = parseCountryTokens(rawConfig && rawConfig.customCountries);
    if (!tokens.length) return { valid: false, reason: 'missing-country-list', profile, countries: new Set() };
    const countryIds = new Set();
    for (const token of tokens) {
      const resolved = resolveCountry(token, indexes);
      if (resolved.status !== 'resolved') {
        return {
          valid: false,
          reason: resolved.status === 'ambiguous-iso' || resolved.status === 'ambiguous-alias' ? 'ambiguous-country-list' : 'unresolved-country-list',
          profile,
          countries: new Set(),
          token
        };
      }
      countryIds.add(resolved.country.id);
    }
    return { valid: true, profile, countries: countryIds };
  }

  /** @returns {TravelCheckerResult} */
  function evaluateCompatibility(countryOrToken, rawConfig, dataset, indexes) {
    const configuration = prepareConfiguration(rawConfig, indexes);
    if (!configuration.valid) return { state: 'unavailable', reason: configuration.reason, profile: configuration.profile || null, country: null, plugs: [] };
    const resolution = resolveCountry(countryOrToken, indexes);
    if (resolution.status !== 'resolved') {
      return {
        state: 'unavailable',
        reason: resolution.status === 'ambiguous-iso' || resolution.status === 'ambiguous-alias' ? 'ambiguous-country' : 'unknown-country',
        profile: configuration.profile,
        country: null,
        plugs: []
      };
    }
    const country = resolution.country;
    const plugs = Array.isArray(country.plugs) ? country.plugs.slice() : [];
    if (!plugs.length) return { state: 'unavailable', reason: 'missing-plugs', profile: configuration.profile, country, plugs: [] };

    // Explicit Please Check ALWAYS takes precedence
    if (country.is_please_check) return { state: 'warning', reason: 'please-check', profile: configuration.profile, country, plugs };

    const profiles = (dataset && dataset.profiles) || {};
    const universalExclusions = new Set(profiles.universal_exclusion_ids || []);
    let compatible = false;

    if (configuration.profile === 'universal') {
      compatible = !universalExclusions.has(country.id);
    } else if (PRESET_PLUGS[configuration.profile]) {
      const targetPlug = PRESET_PLUGS[configuration.profile];
      compatible = plugs.includes(targetPlug);
    } else if (configuration.profile === 'custom_include') {
      compatible = configuration.countries.has(country.id);
    } else if (configuration.profile === 'custom_exclude') {
      compatible = !configuration.countries.has(country.id);
    }

    return { state: compatible ? 'yes' : 'no', reason: compatible ? 'compatible' : 'excluded', profile: configuration.profile, country, plugs };
  }

  function formatPlugs(plugs, locale) {
    const values = (plugs || []).map((plug) => String(plug).trim()).filter(Boolean);
    if (!values.length) return '';
    const localized = values.map((plug) => locale === 'nl' ? `stekker type ${plug}` : `Type ${plug} plug`);
    if (localized.length === 1) return localized[0];
    if (localized.length === 2) return localized.join(locale === 'nl' ? ' en ' : ' and ');
    const finalJoin = locale === 'nl' ? ' en ' : ', and ';
    return `${localized.slice(0, -1).join(', ')}${finalJoin}${localized.at(-1)}`;
  }

  function interpolate(template, values) {
    return String(template || '').replace(/(?:{{\s*|\[\[\s*)(country|plugs)(?:\s*}}|\s*\]\])/g, (_, name) => values[name] || '');
  }

  function filterCountries(query, dataset, locale) {
    const search = key(query);
    if (!search) return [];
    const display = localeFor(locale);
    const altDisplay = display === 'nl' ? 'en' : 'nl';

    const score = (country) => {
      const localName = key(country[`name_${display}`]);
      const altName = key(country[`name_${altDisplay}`]);
      const iso = key(country.iso);
      const aliases = (country.aliases || []).map((a) => key(a)).filter(Boolean);

      // Tier 0: exact localized country name
      if (localName === search) return 0;
      // Tier 1: exact alternate-language country name
      if (altName === search) return 1;
      // Tier 2: exact ISO
      if (iso === search) return 2;
      // Tier 3: exact alias
      if (aliases.includes(search)) return 3;
      // Tier 4: localized-name prefix
      if (localName.startsWith(search)) return 4;
      // Tier 5: alternate-name prefix
      if (altName.startsWith(search)) return 5;
      // Tier 6: alias prefix / ISO prefix
      if (iso.startsWith(search) || aliases.some((a) => a.startsWith(search))) return 6;
      // Tier 7: localized or alternate name substring
      if (localName.includes(search) || altName.includes(search)) return 7;
      // Tier 8: alias substring
      if (aliases.some((a) => a.includes(search))) return 8;
      // Tier 9: no match
      return 9;
    };

    return (dataset.countries || [])
      .filter((country) => score(country) < 9)
      .sort((left, right) => {
        const order = score(left) - score(right);
        if (order !== 0) return order;
        return String(left[`name_${display}`]).localeCompare(String(right[`name_${display}`]), display);
      });
  }

  const SPECIAL_FLAG_CODES = {
    'bonaire--bonaire--bq': 'BQ-BO',
    'saba--saba--bq': 'BQ-SA',
    'niger--niger--ng': 'NE',
    'kenya--kenia--ke': null
  };

  function getFlagCode(country) {
    if (!country) return null;
    if (country.id && Object.prototype.hasOwnProperty.call(SPECIAL_FLAG_CODES, country.id)) {
      return SPECIAL_FLAG_CODES[country.id];
    }
    const iso = String(country.iso || '').trim().toUpperCase();
    return iso || null;
  }

  function createGlobeSvg(extraClass) {
    const className = extraClass ? `travel-checker__globe-icon-midevv-kn ${extraClass}` : 'travel-checker__globe-icon-midevv-kn';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('class', className);
    svg.innerHTML = '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.75"/><ellipse cx="12" cy="12" rx="4.2" ry="9" fill="none" stroke="currentColor" stroke-width="1.75"/><path d="M3.2 9h17.6M3.2 15h17.6" stroke="currentColor" stroke-width="1.75"/>';
    return svg;
  }

  function createFlagBadge(country) {
    const code = getFlagCode(country);
    if (!code) {
      return createGlobeSvg('travel-checker__flag-badge-midevv-kn');
    }
    const span = document.createElement('span');
    span.className = `country-flags country-flags--${code} travel-checker__flag-badge-midevv-kn`;
    span.setAttribute('aria-hidden', 'true');
    return span;
  }

  function createResultIcon(state) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('class', 'travel-checker__result-svg-midevv-kn');
    if (state === 'yes') {
      svg.innerHTML = '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/><path d="m8 12.2 2.7 2.7L16.2 9.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    } else if (state === 'no') {
      svg.innerHTML = '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/><path d="m9 9 6 6m0-6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" fill="none"/>';
    } else {
      svg.innerHTML = '<path d="M12 3.8 2.8 19.8h18.4L12 3.8Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" fill="none"/><path d="M12 9.2v4.8m0 3v.3" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>';
    }
    return svg;
  }

  class VoomyTravelChecker extends HTMLElementBase {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.setDebugState('initializing');
      if (!shouldRenderForConfig(this.runtimeConfig)) {
        this.hidden = true;
        this.setDebugState('disabled');
        return;
      }
      this.locale = localeFor(this.runtimeConfig.locale || document.documentElement.lang);
      this.copy = this.getCopy();
      this.render();
      this.bind();
      this.loadData();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    get runtimeConfig() {
      try { return JSON.parse(this.getAttribute('data-config') || '{}'); } catch (_) { return {}; }
    }

    get isDebug() {
      return this.getAttribute('data-debug') === 'true';
    }

    setDebugState(state) {
      if (!this.isDebug) return;
      this.setAttribute('data-js-initialized', 'true');
      this.setAttribute('data-data-state', state);
    }

    getCopy() {
      const language = this.locale;
      const fromAttributes = {};
      for (const name of ['title', 'label', 'placeholder', 'clear', 'empty', 'unavailable', 'yes', 'no', 'warning', 'noTemplate', 'warningTemplate', 'selected', 'results', 'search']) {
        const attributeName = `data-copy-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}-${language}`;
        if (this.hasAttribute(attributeName)) fromAttributes[name] = this.getAttribute(attributeName);
      }
      return { ...DEFAULT_COPY[language], ...fromAttributes };
    }

    render() {
      const id = this.id || `travel-checker-${Math.random().toString(36).slice(2)}`;
      const rawStyle = this.getAttribute('data-style') || 'minimal';
      const style = ['card', 'accent', 'glass'].includes(rawStyle) ? rawStyle : 'minimal';
      const rawDensity = this.getAttribute('data-density') || 'standard';
      const density = rawDensity === 'compact' ? 'compact' : 'standard';

      this.innerHTML = `
        <section class="travel-checker-midevv-kn travel-checker--${style}-midevv-kn travel-checker--density-${density}-midevv-kn" aria-labelledby="${id}-title" data-style="${style}" data-density="${density}">
          <div class="travel-checker__header-midevv-kn">
            <h2 class="travel-checker__heading-midevv-kn" id="${id}-title"></h2>
            <p class="travel-checker__label-midevv-kn" id="${id}-desc"></p>
          </div>
          <div class="travel-checker__field-wrap-midevv-kn">
            <label class="travel-checker__visually-hidden-midevv-kn" for="${id}-input"></label>
            <div class="travel-checker__input-wrap-midevv-kn">
              <div class="travel-checker__input-leading-midevv-kn" aria-hidden="true">
                <svg aria-hidden="true" class="travel-checker__search-icon-midevv-kn" viewBox="0 0 24 24">
                  <path d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" fill="none"/>
                </svg>
                <div class="travel-checker__selected-flag-midevv-kn" hidden></div>
              </div>
              <input class="travel-checker__field-midevv-kn" id="${id}-input" type="text" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="${id}-results" aria-describedby="${id}-desc" autocomplete="off" spellcheck="false">
              <button class="travel-checker__clear-midevv-kn" type="button" hidden><span aria-hidden="true">×</span></button>
            </div>
            <div class="travel-checker__options-midevv-kn" id="${id}-results" role="listbox" hidden></div>
            <p class="travel-checker__status-midevv-kn" aria-live="polite"></p>
          </div>
          <div class="travel-checker__result-midevv-kn" aria-live="polite" hidden></div>
        </section>
      `;

      this.container = this.querySelector('.travel-checker-midevv-kn');
      this.heading = this.querySelector('.travel-checker__heading-midevv-kn');
      this.label = this.querySelector('.travel-checker__label-midevv-kn');
      this.srLabel = this.querySelector('.travel-checker__visually-hidden-midevv-kn');
      this.input = this.querySelector('.travel-checker__field-midevv-kn');
      this.searchIcon = this.querySelector('.travel-checker__search-icon-midevv-kn');
      this.selectedFlag = this.querySelector('.travel-checker__selected-flag-midevv-kn');
      this.clearButton = this.querySelector('.travel-checker__clear-midevv-kn');
      this.options = this.querySelector('.travel-checker__options-midevv-kn');
      this.status = this.querySelector('.travel-checker__status-midevv-kn');
      this.result = this.querySelector('.travel-checker__result-midevv-kn');

      this.heading.textContent = this.copy.title;
      this.label.textContent = this.copy.label;
      this.srLabel.textContent = this.copy.label;
      this.input.placeholder = this.copy.placeholder;
      this.input.setAttribute('aria-label', this.copy.search);
      this.clearButton.setAttribute('aria-label', this.copy.clear);
      this.options.setAttribute('aria-label', this.copy.results);
      this.matches = [];
      this.activeIndex = -1;
    }

    bind() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.input.addEventListener('input', () => this.search(), { signal });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.input.addEventListener('blur', () => window.setTimeout(() => this.closeOptions(), 140), { signal });
      this.clearButton.addEventListener('click', () => this.clear(), { signal });
      this.options.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
      this.options.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.selectById(option.dataset.countryId);
      }, { signal });
    }

    async loadData() {
      const source = this.getAttribute('data-source');
      if (!source) return this.showUnavailable();
      try {
        this.setDebugState('loading');
        const response = await fetch(source, { signal: this.abortController.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('country-data-unavailable');
        const payload = await response.json();
        if (!isUsableDataset(payload)) throw new Error('invalid-country-data');
        this.countryData = payload;
        this.indexes = buildIndexes(this.countryData);
        this.status.textContent = '';
        this.setDebugState('loaded');
      } catch (_) {
        if (!this.abortController.signal.aborted) {
          this.setDebugState('failed');
          this.showUnavailable();
        }
      }
    }

    search() {
      this.result.hidden = true;
      this.clearButton.hidden = !this.input.value;
      if (this.selectedFlag) {
        this.selectedFlag.hidden = true;
        this.selectedFlag.replaceChildren();
        if (this.searchIcon) this.searchIcon.hidden = false;
        if (this.container) this.container.classList.remove('travel-checker--has-selection-midevv-kn');
      }
      if (!this.countryData || !this.input.value.trim()) return this.closeOptions();
      this.matches = filterCountries(this.input.value, this.countryData, this.locale).slice(0, 12);
      this.activeIndex = this.matches.length ? 0 : -1;
      this.renderOptions();
    }

    renderOptions() {
      this.options.replaceChildren();
      if (!this.matches.length) {
        const empty = document.createElement('p');
        empty.className = 'travel-checker__empty-midevv-kn';
        empty.textContent = this.copy.empty;
        this.options.append(empty);
      } else {
        this.matches.forEach((country, index) => {
          const option = document.createElement('button');
          option.type = 'button';
          option.role = 'option';
          option.dataset.countryId = country.id;
          option.id = `${this.id}-option-${index}`;
          option.className = 'travel-checker__option-midevv-kn';
          option.setAttribute('aria-selected', String(index === this.activeIndex));

          const flagEl = createFlagBadge(country);

          const nameSpan = document.createElement('span');
          nameSpan.className = 'travel-checker__option-name-midevv-kn';
          nameSpan.textContent = country[`name_${this.locale}`];

          const isoSpan = document.createElement('span');
          isoSpan.className = 'travel-checker__option-iso-midevv-kn';
          isoSpan.setAttribute('aria-hidden', 'true');
          isoSpan.textContent = country.iso ? country.iso.toUpperCase() : '';

          option.append(flagEl, nameSpan, isoSpan);
          this.options.append(option);
        });
      }
      this.options.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.updateActiveDescendant();
    }

    onKeydown(event) {
      if (event.key === 'Escape') { this.closeOptions(); return; }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!this.matches.length) return;
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + direction + this.matches.length) % this.matches.length;
        this.renderOptions();
      }
      if (event.key === 'Enter' && this.activeIndex >= 0 && this.matches[this.activeIndex]) {
        event.preventDefault(); this.select(this.matches[this.activeIndex]);
      }
      if (event.key === 'Tab') this.closeOptions();
    }

    updateActiveDescendant() {
      const active = this.options.querySelector(`[data-country-id="${this.matches[this.activeIndex] && this.matches[this.activeIndex].id}"]`);
      if (active) this.input.setAttribute('aria-activedescendant', active.id);
      else this.input.removeAttribute('aria-activedescendant');
    }

    selectById(id) {
      const country = this.indexes.byId.get(id);
      if (country) this.select(country);
    }

    select(country) {
      const countryName = country[`name_${this.locale}`];
      this.input.value = countryName;
      this.clearButton.hidden = false;
      if (this.selectedFlag) {
        this.selectedFlag.replaceChildren(createFlagBadge(country));
        this.selectedFlag.hidden = false;
        if (this.searchIcon) this.searchIcon.hidden = true;
        if (this.container) this.container.classList.add('travel-checker--has-selection-midevv-kn');
      }
      this.closeOptions();
      const result = evaluateCompatibility(country, this.runtimeConfig, this.countryData, this.indexes);
      this.renderResult(result);
    }

    closeOptions() {
      this.options.hidden = true;
      this.options.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }

    clear() {
      this.input.value = '';
      this.clearButton.hidden = true;
      this.result.hidden = true;
      this.status.textContent = '';
      if (this.selectedFlag) {
        this.selectedFlag.hidden = true;
        this.selectedFlag.replaceChildren();
        if (this.searchIcon) this.searchIcon.hidden = false;
        if (this.container) this.container.classList.remove('travel-checker--has-selection-midevv-kn');
      }
      this.closeOptions();
      this.input.focus();
    }

    showUnavailable() {
      this.status.textContent = this.copy.unavailable;
      this.result.hidden = true;
    }

    renderResult(result) {
      if (result.state === 'unavailable') return this.showUnavailable();
      const countryName = result.country[`name_${this.locale}`];
      const plugs = formatPlugs(result.plugs, this.locale);
      const heading = result.state === 'yes' ? this.copy.yes : result.state === 'no' ? this.copy.no : this.copy.warning;
      const explanation = result.state === 'yes' ? '' : interpolate(result.state === 'no' ? this.copy.noTemplate : this.copy.warningTemplate, { country: countryName, plugs });
      this.result.className = `travel-checker__result-midevv-kn travel-checker__result--${result.state}-midevv-kn`;
      const icon = document.createElement('span');
      icon.className = 'travel-checker__result-icon-midevv-kn';
      icon.setAttribute('aria-hidden', 'true');
      icon.replaceChildren(createResultIcon(result.state));
      const details = document.createElement('div');
      details.className = 'travel-checker__result-body-midevv-kn';
      const title = document.createElement('strong');
      title.className = 'travel-checker__result-title-midevv-kn';
      title.textContent = heading;
      details.append(title);
      if (explanation) {
        const message = document.createElement('p');
        message.className = 'travel-checker__result-message-midevv-kn';
        message.textContent = explanation;
        details.append(message);
      }
      this.result.replaceChildren(icon, details);
      this.result.hidden = false;
      this.status.textContent = interpolate(this.copy.selected, { country: countryName });
    }
  }

  const api = {
    normalize,
    key,
    localeFor,
    normalizeProfile,
    parseCountryTokens,
    shouldRenderForConfig,
    isUsableDataset,
    buildIndexes,
    resolveCountry,
    prepareConfiguration,
    evaluateCompatibility,
    formatPlugs,
    filterCountries,
    interpolate,
    getFlagCode
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.VoomyTravelChecker = api;
  if (typeof customElements !== 'undefined' && !customElements.get('voomy-travel-checker')) customElements.define('voomy-travel-checker', VoomyTravelChecker);
})(typeof window !== 'undefined' ? window : globalThis);
